import { RPCs } from "../../../services/rpc.js";
import { useRPCContext } from "../../../contexts/RPCContext.js";
import { useRPC } from "../../../hooks/useRPC/index.js";
import { useStateDebounced } from "../../../hooks/useStateDebounced.js";
import { createMockReactSyntheticEvent } from "../../../utils/mock-react-synthetic-event.js";
import { SelectLocationRenderComponents } from "./SelectLocation.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Select } from "@therms/atalaya";
import { jsx } from "react/jsx-runtime";
import { castArray } from "lodash-es";
//#region src/modules/Locations/shared/SelectLocationFromLocationAndGroupIds.tsx
var SelectLocationFromLocationAndGroupIds = ({ active = true, limit = 25, locationAndGroupIds, multi, onChange, onChangeOptions, onChangeValue, value, ...atalayaSelectProps }) => {
	const [, debouncedSearchText, setSearchText] = useStateDebounced("");
	const [selectedLocations, setSelectedLocations] = useState([]);
	const [skip, setSkip] = useState(0);
	const [options, setOptions] = useState([]);
	const [serverTotal, setServerTotal] = useState(0);
	const prevDataRef = useRef(void 0);
	const { rpcService } = useRPCContext();
	const locationAndGroupIdsKey = JSON.stringify(locationAndGroupIds);
	const stableLocationAndGroupIds = useMemo(() => locationAndGroupIds, [locationAndGroupIdsKey]);
	const query = useMemo(() => ({
		active,
		locationAndGroupIdFilter: stableLocationAndGroupIds,
		pagination: {
			limit,
			skip
		},
		searchText: debouncedSearchText
	}), [
		active,
		debouncedSearchText,
		stableLocationAndGroupIds,
		limit,
		skip
	]);
	const { data, isCalling: loading } = useRPC(RPCs.Locations.GetLocationsFromLocationAndGroupIdsList.v1, query);
	useEffect(() => {
		if (!data?.locations || data === prevDataRef.current) return;
		prevDataRef.current = data;
		setServerTotal(data.total ?? data.locations.length);
		if (skip > 0) setOptions((prev) => [...prev, ...data.locations]);
		else setOptions(data.locations);
	}, [data, skip]);
	useEffect(() => {
		setSkip(0);
	}, [
		debouncedSearchText,
		stableLocationAndGroupIds,
		active
	]);
	useEffect(() => {
		if (!value) {
			setSelectedLocations([]);
			return;
		}
		const _value = castArray(value);
		const fetchedLocations = [];
		const idsToFetch = [];
		_value.forEach((selectedId) => {
			const found = options.find((location) => location.id === selectedId);
			if (found) fetchedLocations.push(found);
			else idsToFetch.push(selectedId);
		});
		setSelectedLocations(fetchedLocations);
		if (idsToFetch.length) rpcService.Locations.GetLocationsFromLocationAndGroupIdsList.v1.call({ locationAndGroupIdFilter: castArray(value) }).then((_response) => {
			setSelectedLocations([...fetchedLocations, ..._response.data.locations]);
		});
	}, [value]);
	const handleLoadOptions = useCallback(({ additional, text } = {}) => {
		if (text !== debouncedSearchText) {
			setSearchText(text || "");
			return;
		}
		if (loading) return;
		if (additional && serverTotal > options.length) setSkip(skip + limit);
	}, [
		debouncedSearchText,
		limit,
		loading,
		options.length,
		serverTotal,
		skip
	]);
	const handleOnChange = async (option) => {
		let value;
		if (Array.isArray(option)) value = option.map((opt) => opt.id);
		else if (option) value = option.id;
		else if (multi) value = [];
		else value = null;
		if (onChangeValue) onChangeValue(value);
		if (onChangeOptions) onChangeOptions(option);
		if (onChange) onChange(createMockReactSyntheticEvent({ value }));
	};
	return /* @__PURE__ */ jsx(Select, {
		RenderListItem: SelectLocationRenderComponents.ListItem,
		RenderSelectedMulti: SelectLocationRenderComponents.SelectedMulti,
		RenderSelectedSingle: SelectLocationRenderComponents.SelectedSingle,
		isLoading: loading,
		isSearchable: true,
		loadOptions: handleLoadOptions,
		multi,
		optionLabelExtractor: ({ name }) => name,
		optionValueExtractor: ({ id }) => id,
		options,
		value: selectedLocations,
		filterOption: null,
		onChangeOptions: handleOnChange,
		subText: atalayaSelectProps.subText,
		...atalayaSelectProps,
		allowCreate: false
	});
};
//#endregion
export { SelectLocationFromLocationAndGroupIds };

//# sourceMappingURL=SelectLocationFromLocationAndGroupIds.js.map